import { format } from "date-fns"
import { AlertTriangle, Truck } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { Location, SlotService } from "@/types/collections"

import LocationPicker from "./LocationPicker"
import type { AdHocStopType, useAdHocStopForm } from "./useAdHocStopForm"

interface AdHocStopDialogProps {
  adHocStop: ReturnType<typeof useAdHocStopForm>
  locations: Location[]
}

export default function AdHocStopDialog({ adHocStop, locations }: AdHocStopDialogProps) {
  const {
    open,
    setOpen,
    form,
    setForm,
    error,
    locationSearchTerm,
    setLocationSearchTerm,
    selectedRoute,
    serviceTime,
    needsServiceConfig,
    canSubmit,
    submit,
  } = adHocStop

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Stop</DialogTitle>
          <DialogDescription>
            {form.routeId && form.arrivalTime && form.departureTime ? (
              <span>
                Scheduled from {form.arrivalTime} to {form.departureTime}
              </span>
            ) : (
              <span>Add a new stop to an existing route</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {selectedRoute && (
            <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
              <div className="mb-1 flex items-center gap-2 text-xs text-palette-jungleGreen500">
                <Truck size={12} />
                Route
              </div>
              <div className="font-medium text-palette-jungleGreen900">
                {selectedRoute.code}
              </div>
              <div className="text-xs text-palette-jungleGreen500">
                {selectedRoute.driverName} •{" "}
                {format(selectedRoute.plannedStartTime, "HH:mm")}-
                {format(selectedRoute.plannedEndTime, "HH:mm")}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="arrivalTime">Start Time *</Label>
              <Input
                id="arrivalTime"
                type="time"
                value={form.arrivalTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, arrivalTime: e.target.value }))
                }
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="departureTime">End Time *</Label>
              <Input
                id="departureTime"
                type="time"
                value={form.departureTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, departureTime: e.target.value }))
                }
                className="bg-white"
              />
            </div>
          </div>

          <LocationPicker
            locations={locations}
            selectedId={form.locationId}
            onSelect={(locationId) => setForm((prev) => ({ ...prev, locationId }))}
            searchTerm={locationSearchTerm}
            onSearchChange={setLocationSearchTerm}
          />

          <div className="grid gap-2">
            <Label htmlFor="stopType">Slot Type *</Label>
            <Select
              value={form.adHocStopType}
              onValueChange={(value: AdHocStopType) =>
                setForm((prev) => ({ ...prev, adHocStopType: value }))
              }
            >
              <SelectTrigger id="stopType" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="shipper_collection">Shipper Collection</SelectItem>
                <SelectItem value="internal_stop">Internal Stop</SelectItem>
                <SelectItem value="haulage_collection">Haulage Collection</SelectItem>
                <SelectItem value="haulage_delivery">Haulage Delivery</SelectItem>
                <SelectItem value="depot_injection">Depot Injection</SelectItem>
                <SelectItem value="direct_injection">Direct Injection</SelectItem>
                <SelectItem value="charging_stop">Charging Stop</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-palette-jungleGreen000 p-3">
            <span className="text-sm text-palette-jungleGreen600">Service Time</span>
            <span className="font-medium text-palette-jungleGreen900">
              {serviceTime} minutes
            </span>
          </div>

          {needsServiceConfig && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="slotServices">Service Classes *</Label>
                <p className="-mt-1 mb-2 text-xs text-palette-jungleGreen500">
                  Select service classes for this stop (e.g., SD, 2D, ND)
                </p>
                <div className="flex flex-wrap gap-2">
                  {(["SD", "2D", "ND"] as SlotService[]).map((service) => {
                    const isSelected = form.slotServices.includes(service)
                    return (
                      <button
                        key={service}
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            slotServices: isSelected
                              ? prev.slotServices.filter((s) => s !== service)
                              : [...prev.slotServices, service],
                          }))
                        }}
                        className={cn(
                          "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                          isSelected
                            ? "bg-amber-500 text-white hover:bg-amber-600"
                            : "bg-palette-jungleGreen100 text-palette-jungleGreen700 hover:bg-palette-jungleGreen200"
                        )}
                      >
                        {service}
                      </button>
                    )
                  })}
                </div>
                {form.slotServices.length > 0 && (
                  <p className="mt-1 text-xs text-palette-jungleGreen500">
                    Selected: {form.slotServices.join(", ")}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="slotPnl">P&L Category *</Label>
                <p className="-mt-1 mb-2 text-xs text-palette-jungleGreen500">
                  Select P&L categories for this stop (required)
                </p>
                <div className="flex flex-wrap gap-2">
                  {["RC1", "RC2", "HIVED One", "HIVED Haulage"].map((pnl) => {
                    const isSelected = form.slotPnl.includes(pnl)
                    return (
                      <button
                        key={pnl}
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            slotPnl: isSelected
                              ? prev.slotPnl.filter((p) => p !== pnl)
                              : [...prev.slotPnl, pnl],
                          }))
                        }}
                        className={cn(
                          "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                          isSelected
                            ? "bg-blue-500 text-white hover:bg-blue-600"
                            : "bg-palette-jungleGreen100 text-palette-jungleGreen700 hover:bg-palette-jungleGreen200"
                        )}
                      >
                        {pnl}
                      </button>
                    )
                  })}
                </div>
                {form.slotPnl.length > 0 && (
                  <p className="mt-1 text-xs text-palette-jungleGreen500">
                    Selected: {form.slotPnl.join(", ")}
                  </p>
                )}
              </div>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            variant="confirm"
          >
            Create Stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
